"use client";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  FaFacebook,
  FaLinkedin,
  FaInstagram,
  FaArrowUp,
  FaWhatsapp,
} from "react-icons/fa";
import logo from "../../images/logo.png";
import Image from "next/image";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [isScrolled, setIsScrolled] = useState(false);

  // Inside your Footer component:
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsScrolled(true); // Show both buttons when scrolled down
      } else {
        setIsScrolled(false); // Hide both buttons when near top
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const footerLinks = {
    services: ["Web Development", "Mobile Apps", "SEO", "UI/UX Design"],
    company: ["About Us", "Testimonials", "Our Impact", "Contact Us"],
    social: [
      { icon: <FaFacebook />, name: "Facebook", color: "#1877F2" },
      { icon: <FaLinkedin />, name: "LinkedIn", color: "#0077B5" },
      { icon: <FaInstagram />, name: "Instagram", color: "#E1306C" },
    ],
  };

  return (
    <footer className="footer">
      <div className="footer__container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="footer__content"
        >
          <div className="footer__brand">
            <motion.a href="#" className="footer__logo-wrapper">
              <div className="footer__logo-combo">
                <Image
                  src={logo}
                  alt="Perftecht Solutions logo"
                  className="footer__logo-icon"
                  width={48}
                  height={48}
                />
                <span className="footer__logo-text">Perftecht Solutions</span>
              </div>
            </motion.a>

            <p className="footer__tagline">
              Elevating digital experiences through innovation
            </p>

            <motion.div
              className="footer__social-icons"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
            >
              {footerLinks.social.map((social) => (
                <motion.a
                  key={social.name}
                  variants={{
                    hidden: { y: 20, opacity: 0 },
                    visible: { y: 0, opacity: 1 },
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                  whileHover={{
                    y: -5,
                  }}
                  href={
                    social.name === "Facebook"
                      ? "https://facebook.com/perftechtsolutions"
                      : social.name === "LinkedIn"
                      ? "https://linkedin.com/company/perftecht-solutions"
                      : "https://instagram.com/perftecht.solutions"
                  }
                  aria-label={social.name}
                  className="footer__social-icon"
                >
                  {social.icon}
                </motion.a>
              ))}
            </motion.div>
          </div>

          <div className="footer__links-grid">
            <motion.div
              className="footer__links-group"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="footer__links-title">Services</h4>
              <ul className="footer__links-list">
                {footerLinks.services.map((item) => (
                  <motion.li
                    key={item}
                    whileHover={{ x: 5 }}
                    className="footer__links-item"
                  >
                    <a href={`#services`} className="footer__link">
                      {item}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="footer__links-group"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="footer__links-title">Company</h4>
              <ul className="footer__links-list">
                {footerLinks.company.map((item) => (
                  <motion.li
                    key={item}
                    whileHover={{ x: 5 }}
                    className="footer__links-item"
                  >
                    <a
                      href={`#${item.toLowerCase().replace(" ", "-")}`}
                      className="footer__link"
                    >
                      {item}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          className="footer__bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="footer__copyright">
            &copy; {currentYear} Perftecht Solutions. All rights reserved.
          </p>
        </motion.div>
      </div>

      {/* Back to top button with WhatsApp icon for mobile and tablet */}
      <motion.button
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className={`footer__back-to-top ${isScrolled ? "visible" : ""}`}
      >
        <FaArrowUp />
      </motion.button>

      <motion.a
        href="https://wa.me/61493370722" // Replace with your WhatsApp number link
        target="_blank"
        className={`footer__whatsapp-icon ${isScrolled ? "visible" : ""}`}
      >
        <FaWhatsapp />
      </motion.a>
    </footer>
  );
}
